import React, {
  ChangeEvent,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { HITS_PRE_LOAD } from 'utils/constants';
import facetsMapper from 'utils/facetsMapper';
import fetchElastic from 'utils/fetchElastic';
import getAllQueryParams from 'utils/getAllParamsFromURL';
import eventsBodyParser from 'utils/parsers/eventsBodyParser';
import facetsBodyParser from 'utils/parsers/facetsBodyParser';
import {
  EventBodyParser,
  FacetsBodyParser,
  SearchWithFacetsBodyParser,
} from 'utils/parsers/models';
import searchWithFacetsBodyParser from 'utils/parsers/searchWithFacetsBodyParser';
import removeAllQueryParams from 'utils/removeAllQueryParams';
import setCurrentFacetsList from 'utils/setCurrentFacetsList';
import setOnlyOneQueryParam from 'utils/setOnlyOneQueryParam';
import updateQueryParamInUrl from 'utils/updateQueryParamInURL';

import { ElasticContextType, Facet, FacetList, FacetWithKey, SortOrder } from './elasticModels';

export const ElasticContext = createContext<ElasticContextType | undefined>(undefined);

export const useElasticContext = () => {
  const context = useContext(ElasticContext);
  if (!context) {
    throw new Error('useElasticContext must be used within an ElasticContextProvider');
  }

  return context;
};

export const ElasticContextProvider = ({
  children,
  indexName,
  filters,
  pageId,
  numberOfResultsPerLoad,
  resultsSubTags,
}) => {
  const [facetsValue, setFacetsValue] = useState<FacetList | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [inputValue, setInputValue] = useState<string>('');
  const [dataHits, setDataHits] = useState<any[] | null>(null);
  const [totalValue, setTotalValue] = useState<number>(0);
  const [firstLoading, setFirstLoading] = useState<boolean>(false);
  const [showTotalHits, setShowTotalHits] = useState<boolean>(false);
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [isUpcoming, setIsUpcoming] = useState<boolean>(true);
  const [sortBy, setSortBy] = useState<string>(`${process.env.GATSBY_ELASTIC_INDEX}-events`);
  const [facetsInitialized, setFacetsInitialized] = useState(false);

  const HITS_PER_PAGE = useMemo(
    () => numberOfResultsPerLoad || HITS_PRE_LOAD,
    [numberOfResultsPerLoad]
  );

  const loadMore = useMemo(() => dataHits && totalValue > dataHits.length, [dataHits, totalValue]);
  const setDefaultPageValue = useCallback((): void => {
    setCurrentPage(1);
    updateQueryParamInUrl(`page`, 1);
  }, []);

  const paginationPagesAmount = useMemo(() => Math.ceil(totalValue / HITS_PER_PAGE), [totalValue]);

  const handleSetSortOrder = useCallback((value: SortOrder): void => setSortOrder(value), []);
  const handleSetSortBy = useCallback((value: string): void => setSortBy(value), []);

  const handleSetUpcomingValue = useCallback(() => {
    setIsUpcoming((prev) => !prev);
  }, []);

  const activeFacetsList = useMemo<Facet[] | null>(
    () => setCurrentFacetsList(facetsValue),
    [facetsValue]
  );

  const handleChangeInputValue = useCallback((e: ChangeEvent<HTMLInputElement>): void => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setOnlyOneQueryParam('query', newValue);
    setDefaultPageValue();
  }, []);

  const changeFacetsValue = useCallback((filterName: string, label: string) => {
    updateQueryParamInUrl(`refinementList[${filterName}]`, label);
    setDefaultPageValue();

    setFacetsValue((prev) => {
      if (prev === null) {
        return null;
      }

      return {
        ...prev,
        [filterName]: prev[filterName].map((item) =>
          item.label === label ? { ...item, isRefined: !item.isRefined } : item
        ),
      };
    });
  }, []);

  const resetAllFacets = (facetKey?: string, label?: string) => {
    setFacetsValue((prev) => {
      if (!prev) return prev;

      const reset = {};

      Object.keys(prev).forEach((key) => {
        reset[key] = prev[key].map((facet) => {
          let shouldRefine = facetKey && label && key === facetKey && facet.label === label;

          if (shouldRefine === undefined) {
            shouldRefine = false;
          }

          return {
            ...facet,
            isRefined: shouldRefine,
          };
        });
      });

      return reset;
    });
    setDefaultPageValue();
  };

  const remainOnlyOneFacet = (filterName: string, label: string): void => {
    setOnlyOneQueryParam(filterName, label);
    resetAllFacets(filterName, label);
    setInputValue('');
  };

  const removeOnlyOneFacet = useCallback(
    (facet: FacetWithKey): void => {
      if (facetsValue && facet.key) {
        setFacetsValue((prev) => {
          if (!prev) return prev;

          return {
            ...prev,
            [facet.key]: prev[facet.key].map((el) =>
              el.label === facet.label ? { ...el, isRefined: false } : el
            ),
          };
        });

        updateQueryParamInUrl(`refinementList[${facet.key}]`, facet.label);
      }
    },
    [facetsValue]
  );

  const clearFiltersButton = useCallback((): void => {
    removeAllQueryParams();
    resetAllFacets();
    setInputValue('');
  }, []);

  const handleLoadMoreHits = (): void => {
    setCurrentPage((prev) => +prev + 1);
    updateQueryParamInUrl(`page`, +currentPage + 1);
  };

  const handleSetPage = (value: number): void => {
    setCurrentPage(value);
    updateQueryParamInUrl(`page`, value);
  };

  // Fetching facets
  useEffect(() => {
    if (indexName && filters) {
      const loadFacets = async () => {
        if (filters && indexName) {
          const facetsBody: FacetsBodyParser = {
            filters,
            pageId,
            resultsSubTags,
          };
          try {
            const data = await fetchElastic(indexName, facetsBodyParser(facetsBody));

            return setFacetsValue(facetsMapper(data.aggregations));
          } catch (error) {
            console.error('Failed to load facets:', error);
          }
        }
      };

      loadFacets();
    }
  }, [indexName, filters]);

  // set isRefined value using the query params
  useEffect(() => {
    if (!facetsValue || facetsInitialized) return;

    const activeQueryParams = getAllQueryParams();

    if (activeQueryParams?.params?.page) {
      setCurrentPage(+activeQueryParams.params.page);
      setFirstLoading(true);
    }
    if (activeQueryParams?.params?.sortBy) {
      setSortBy(activeQueryParams.params.sortBy);
    }
    if (activeQueryParams?.params?.refinementList?.query) {
      setInputValue(activeQueryParams.params.refinementList.query[0]);
    }

    const queryParamKeys = activeQueryParams?.params?.refinementList;

    if (queryParamKeys) {
      const updatedFacets = { ...facetsValue };

      Object.keys(queryParamKeys).forEach((key) => {
        if (updatedFacets[key]) {
          updatedFacets[key] = updatedFacets[key].map((facetValue) => {
            const isActive = queryParamKeys[key].includes(facetValue.label);

            return {
              ...facetValue,
              isRefined: isActive,
            };
          });
        }
      });

      setFacetsValue(updatedFacets);
      setFacetsInitialized(true);
    }
  }, [facetsValue, facetsInitialized]);

  // Fetching articles data
  useEffect(() => {
    const fetchData = async () => {
      if (
        indexName === `${process.env.GATSBY_ELASTIC_INDEX}-articles` ||
        indexName === `${process.env.GATSBY_ELASTIC_INDEX}-policies`
      ) {
        const fetchHitFromIndex =
          currentPage === 1 || firstLoading ? 0 : (currentPage - 1) * HITS_PER_PAGE;

        const searchWithFacetsBody: SearchWithFacetsBodyParser = {
          sortOrder,
          pageId,
          hitsPerPage: firstLoading ? currentPage * HITS_PER_PAGE : HITS_PER_PAGE,
          from: fetchHitFromIndex,
          searchQuery: inputValue,
          facets: facetsValue,
          indexName,
          resultsSubTags,
        };

        try {
          const { hits } = await fetchElastic(
            indexName,
            searchWithFacetsBodyParser(searchWithFacetsBody)
          );

          setTotalValue(hits.total.value);
          const parsedHits = hits.hits.map(({ _source, ...rest }) => ({ ...rest, ..._source }));

          if (firstLoading) {
            setFirstLoading(false);
          }

          if (currentPage === 1 || firstLoading) {
            return setDataHits(parsedHits);
          }

          return setDataHits((prev) => {
            if (prev === null) return null;

            return [...prev, ...parsedHits];
          });
        } catch (error) {
          console.error('Failed to load hits:', error);
        }
      }
    };

    fetchData();
  }, [facetsValue, inputValue, currentPage, sortOrder]);

  useEffect(() => {
    if (!showTotalHits) {
      if (facetsValue) {
        Object.keys(facetsValue).forEach((key) => {
          facetsValue[key].forEach((element) => {
            if (element.isRefined) {
              setShowTotalHits(true);
            }
          });
        });
      }

      if (inputValue) {
        setShowTotalHits(true);
      }
    }
    if (showTotalHits) {
      let isActiveFacet = false;
      if (facetsValue && !inputValue) {
        Object.keys(facetsValue).forEach((key) => {
          facetsValue[key].forEach((element) => {
            if (element.isRefined) {
              isActiveFacet = true;
            }
          });
        });
      }

      if (!isActiveFacet) {
        setShowTotalHits(false);
      }
    }
  }, [facetsValue, inputValue]);

  // EVENTS and POLICIES
  useEffect(() => {
    if (indexName === `${process.env.GATSBY_ELASTIC_INDEX}-events`) {
      const loadEvents = async () => {
        try {
          const eventBody: EventBodyParser = {
            sortBy,
            hitsPerPage: HITS_PER_PAGE,
            from: HITS_PER_PAGE * (+currentPage - 1),
          };
          const { hits } = await fetchElastic(indexName, eventsBodyParser(eventBody));
          const parsedHits = hits.hits.map(({ _source, ...rest }) => ({ ...rest, ..._source }));
          setDataHits(parsedHits);
          setTotalValue(hits.total.value);
        } catch (error) {
          console.error('Failed to load events:', error);
        }
      };

      loadEvents();
    }
  }, [sortBy, currentPage]);

  const elasticData = {
    facetsValue,
    changeFacetsValue,
    resetAllFacets,
    remainOnlyOneFacet,
    inputValue,
    handleChangeInputValue,
    dataHits,
    totalValue,
    loadMore,
    handleLoadMoreHits,
    showTotalHits,
    clearFiltersButton,
    handleSetSortOrder,
    activeFacetsList,
    removeOnlyOneFacet,
    sortOrder,
    isUpcoming,
    handleSetUpcomingValue,
    paginationPagesAmount,
    sortBy,
    handleSetSortBy,
    handleSetPage,
    currentPage,
  };

  return <ElasticContext.Provider value={elasticData}>{children}</ElasticContext.Provider>;
};
